import { addMinutes, differenceInSeconds } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { useTranslation } from "~/hooks/useTranslation";
import { useValidation } from "~/hooks/useValidation";
import { ENameSpace } from "~/i18n/@types/types";
import { Icon } from "~/modules/Common/Icons";
import { Web3Only } from "~/modules/Common/Layout";
import { useAlert } from "~/modules/Common/Overlay/hooks/useAlert";
import { AccountEnum, ErrorTypes } from "~/modules/Router/Auth/types";
import { connectDesktopWallet } from "~/modules/Router/Auth/utils/auth/connectDesktopWallet";
import { consumeRequestId } from "~/modules/Router/Auth/utils/auth/consumeRequestId";
import { createAuthRequest } from "~/modules/Router/Auth/utils/auth/createAuthRequest";
import { getRequestsRegister } from "~/modules/Router/Auth/utils/auth/getRequestsRegister";
import { verifyAccount } from "~/modules/Router/Auth/utils/auth/verifyAccount";
import { toConnectCommandErrorMessage } from "~/modules/Router/Auth/utils/toConnectCommandErrorMessage";
import { Description } from "~/modules/Router/Onboarding/components/Login";
import { getInputDefaultStyle } from "~/utils/converter";

type FormData = {
  emailAddress: string;
  verifyCode: number | null;
};

type VerifyInfo = {
  id: string;
  key: string;
};

const VERIFY_MINUTES = 5;

type Props = {
  onConnected: (userId?: string) => void;
  onClose: () => void;
};

export const DesktopWalletConnectModal = ({ onConnected, onClose }: Props) => {
  const { openAlert } = useAlert();

  const [isVerifyLoading, setIsVerifyLoading] = useState<boolean>(false);
  const [validationCounter, setValidationCounter] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState(new Date().getTime());
  const [deadline, setDeadline] = useState<Date | null>(null);
  const [verifyInfo, setVerifyInfo] = useState<VerifyInfo | null>(null);
  const [verifyErrorMessage, setVerifyErrorMessage] = useState<string | null>(
    null,
  );

  const { register, formState, getValues, reset, handleSubmit, setFocus } =
    useForm<FormData>({
      mode: "onChange",
      defaultValues: {
        emailAddress: "",
        verifyCode: null,
      },
    });

  const isEmailAddressValid = useMemo<boolean>(
    () =>
      !formState.errors.emailAddress && getValues("emailAddress").length > 0,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formState],
  );

  const emailAddressErrorMessage = useMemo<string>(() => {
    if (getValues("emailAddress").length > 0) {
      return formState.errors.emailAddress?.message ?? "";
    } else {
      return "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState]);

  const countDown = useMemo<{ minutes: number; seconds: number } | null>(() => {
    if (deadline == null) return null;
    const diffInSeconds = differenceInSeconds(deadline, currentTime);

    if (diffInSeconds <= 1) return null;
    const minutes = Math.floor(diffInSeconds / 60);
    const seconds = diffInSeconds - minutes * 60;
    return {
      minutes,
      seconds,
    };
  }, [deadline, currentTime]);

  const is30SecondsLater = useMemo<boolean>(() => {
    if (deadline == null) return false;
    const diffInSeconds = differenceInSeconds(deadline, currentTime);

    return 60 * VERIFY_MINUTES - diffInSeconds > 30;
  }, [deadline, currentTime]);

  useEffect(() => {
    if (formState.dirtyFields.emailAddress) {
      setDeadline(null);
    }
  }, [formState.dirtyFields.emailAddress]);

  useEffect(() => {
    if (!isVerifyLoading && validationCounter > 0) {
      setFocus("verifyCode");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVerifyLoading]);

  useEffect(() => {
    setInterval(() => {
      const now = new Date().getTime();
      setCurrentTime(now);
    }, 1000);
  }, []);

  const verifyEmail = (isResend: boolean) => {
    const formData = getValues();
    reset({ emailAddress: formData.emailAddress, verifyCode: null });
    setIsVerifyLoading(true);
    setVerifyInfo(null);
    setVerifyErrorMessage(null);
    !isResend && setDeadline(null);

    getEmailAddressVerifyInfo({
      emailAddress: formData.emailAddress,
    })
      .then((result) => {
        if (result) {
          setDeadline(addMinutes(currentTime, VERIFY_MINUTES));
          setVerifyInfo(result);
        }
      })
      .finally(() => {
        setIsVerifyLoading(false);
        setValidationCounter((prevState) => prevState + 1);
      });
  };

  const handleVerifyEmail = () => {
    verifyEmail(false);
  };

  const handleResendVerifyEmail = () => {
    verifyEmail(true);
  };

  const onSubmit = () => {
    if (!verifyInfo) return;

    postEmailAddressVerifyCode({
      id: verifyInfo.id,
      key: verifyInfo.key,
      verifyCode: getValues().verifyCode,
    }).then((result) => {
      if (result.ok) {
        onConnected(result.userId);
      } else {
        setVerifyErrorMessage(result.message ?? null);
      }
    });
  };

  const handleConnectWallet = async () => {
    const result = await connectDesktopWallet();
    if (result.ok) {
      onConnected();
    } else {
      const message = toConnectCommandErrorMessage(result.value);

      message && openAlert({ text: message });
    }
  };

  const { t } = useTranslation([ENameSpace.Login] as const);
  const { generateTextValidation } = useValidation();

  let content;
  if (countDown) {
    content = (
      <div className="typo-book-20 absolute right-0 top-1/2 flex -translate-y-1/2 gap-x-3">
        <button
          type="button"
          disabled={
            !verifyErrorMessage &&
            (isVerifyLoading || !isEmailAddressValid || !is30SecondsLater)
          }
          onClick={handleResendVerifyEmail}
          className="text-ciety-wh disabled:text-ciety-dk-60"
        >
          Resend
        </button>
        {!verifyErrorMessage && (
          <span className="text-ciety-lg">
            {countDown.minutes}:{countDown.seconds.toString().padStart(2, "0")}
          </span>
        )}
      </div>
    );
  } else if (!isVerifyLoading) {
    content = (
      <button
        type="button"
        tabIndex={0}
        disabled={!isEmailAddressValid}
        onClick={
          validationCounter > 0 ? handleResendVerifyEmail : handleVerifyEmail
        }
        className="typo-book-20 absolute right-0 top-1/2 -translate-y-1/2 text-ciety-lg disabled:text-ciety-dk-60"
      >
        {t(
          validationCounter > 0
            ? `${ENameSpace.Login}:Root.Form.Buttons.Resend`
            : `${ENameSpace.Login}:Root.Form.Buttons.Next`,
        )}
      </button>
    );
  } else {
    content = null;
  }

  return (
    <article className="fixed left-0 top-0 flex h-full w-full items-center justify-center bg-ciety-bk bg-opacity-80 backdrop-blur-[1.25rem]">
      <button
        type={"button"}
        onClick={onClose}
        className={
          "fixed top-[1.25rem] right-[1.25rem] flex h-[3.75rem] w-[3.75rem] items-center justify-center rounded-full bg-ciety-dk-90"
        }
      >
        <Icon
          iconType="Cancel Close"
          className={"h-[1.563rem] w-[1.563rem] text-ciety-dk-60"}
        />
      </button>
      <div className="flex-center w-[31.25rem] flex-col gap-y-10">
        <Description />
        <form className="w-full space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="w-full">
            <span className="typo-book-20 text-ciety-wh">
              {t(`${ENameSpace.Login}:Root.YourEmail`)}
            </span>
            <div className="relative w-full">
              <input
                type="email"
                placeholder=" helloworld@ciety.io"
                autoComplete="off"
                tabIndex={-1}
                className={`${getInputDefaultStyle({
                  type: "line",
                  error: !!emailAddressErrorMessage,
                })} typo-book-20 h-[3.25rem] w-full pr-[9rem]`}
                {...register("emailAddress", {
                  required: generateTextValidation({
                    type: "required",
                    name: t(
                      `${ENameSpace.Login}:Root.Form.Label.EmailAddress` as TI18nLoginKeys,
                    ),
                  }),
                  pattern: {
                    value: /^\S+@\S+\.\S+$/,
                    message: generateTextValidation({
                      type: "default",
                      name: t(
                        `${ENameSpace.Login}:Root.Form.Label.EmailAddress` as TI18nLoginKeys,
                      ),
                    }),
                  },
                })}
              />
              {content}
            </div>
            {emailAddressErrorMessage && (
              <div className="flex items-center gap-x-1 pt-3 text-ciety-rd">
                <Icon iconType="Alert Sign Circle" className="h-3.5 w-3.5" />
                <span className="typo-book-14">{emailAddressErrorMessage}</span>
              </div>
            )}
          </div>
          {countDown && (
            <div className="w-full">
              <span className="typo-book-20 text-ciety-wh">
                {t(`${ENameSpace.Login}:Root.VerificationCode`)}
              </span>
              <input
                type="text"
                maxLength={6}
                onInput={(e) => {
                  const num = e.currentTarget.value.match(/\d+/);
                  e.currentTarget.value = num ? num[0] : "";
                }}
                placeholder={t(
                  `${ENameSpace.Login}:Root.Form.Placeholder.WeSentVerificationCodeToYourEmail`,
                )}
                autoComplete="off"
                className={`${getInputDefaultStyle({
                  type: "line",
                  error: !!formState.errors.verifyCode || !!verifyErrorMessage,
                })} typo-book-20 h-[3.25rem] w-full`}
                {...register("verifyCode", {
                  required: generateTextValidation({
                    type: "required",
                    name: t(`${ENameSpace.Login}:Root.Form.Label.VerifyCode`),
                  }),
                  pattern: {
                    value: /^\d{6}$/,
                    message: generateTextValidation({
                      type: "default",
                      name: t(`${ENameSpace.Login}:Root.Form.Label.VerifyCode`),
                    }),
                  },
                  shouldUnregister: true,
                })}
              />
              {(formState.errors.verifyCode || verifyErrorMessage) && (
                <div className="flex items-center gap-x-1 pt-3 text-ciety-rd">
                  <Icon iconType="Alert Sign Circle" className="h-3.5 w-3.5" />
                  <span className="typo-book-14">
                    {verifyErrorMessage
                      ? verifyErrorMessage
                      : formState.errors.verifyCode?.message}
                  </span>
                </div>
              )}
            </div>
          )}
          <button
            type="submit"
            disabled={!formState.isValid || !getValues("verifyCode")}
            className="typo-medium-20 h-[3.75rem] w-full rounded-[0.75rem] bg-ciety-lg text-ciety-dk disabled:bg-ciety-dk-90"
          >
            {t(`${ENameSpace.Login}:Root.Form.Buttons.StartYourCIETY`)}
          </button>
        </form>
        <Web3Only>
          <div className="flex w-full items-center justify-between gap-x-5">
            <hr className="w-full border-ciety-dk-60" />
            <span className="typo-book-20 shrink-0 text-ciety-gy">
              {t(`${ENameSpace.Login}:Root.OrSignInWith`)}
            </span>
            <hr className="w-full border-ciety-dk-60" />
          </div>
          <button
            type="button"
            className="flex-center h-[3.75rem] gap-x-2 rounded-[0.75rem] bg-ciety-wh px-[1.875rem]"
            onClick={handleConnectWallet}
          >
            <Icon className="h-5 w-5" iconType="Chains-METAMASK" />
            <span className="typo-medium-20 text-ciety-dk">
              {t(`${ENameSpace.Login}:Root.Form.Buttons.ConnectWallet`)}
            </span>
          </button>
        </Web3Only>
      </div>
    </article>
  );
};

const getEmailAddressVerifyInfo = async ({
  emailAddress,
}: Pick<FormData, "emailAddress">): Promise<VerifyInfo | null> => {
  // step 1. id, key 생성
  const createAuthRequestResult = await createAuthRequest();
  if (!createAuthRequestResult.ok) {
    return null;
  }

  const id = createAuthRequestResult.value.id;
  const key = createAuthRequestResult.value.key;

  // step 2. 인증 요청 등록
  const getRequestsRegisterResult = await getRequestsRegister({
    id,
    key,
    account: AccountEnum.EmailAccount,
    chain: null,
    address: emailAddress,
  });

  if (!getRequestsRegisterResult.ok) {
    return null;
  }

  return { id, key };
};

const postEmailAddressVerifyCode = async ({
  id,
  key,
  verifyCode,
}: VerifyInfo & Pick<FormData, "verifyCode">): Promise<{
  ok: boolean;
  message?: string;
  userId?: string;
}> => {
  // step 3. verify code 전달
  const verifyAccountResult = await verifyAccount({
    id,
    key,
    account: AccountEnum.EmailAccount,
    signatureOrCode: String(verifyCode),
  });
  if (!verifyAccountResult.ok) {
    // @TODO 번역
    return {
      ok: false,
      message:
        verifyAccountResult.value === ErrorTypes.TooManyVerifyRequestError
          ? "You have entered incorrect verification code 5 times. Please try again by clicking ‘Resend’ button. "
          : "Verification code is invalid.",
    };
  }

  // step 4. Cookie 생성
  const consumeRequestIdResult = await consumeRequestId({
    id,
    key,
  });

  if (consumeRequestIdResult.ok) {
    return {
      ok: consumeRequestIdResult.ok,
      userId: consumeRequestIdResult.value.user.id,
    };
  }
  return { ok: consumeRequestIdResult.ok };
};
